import { ChangeDetectorRef, Component, OnInit, Type, ViewChild, isDevMode } from '@angular/core';
import { NgComponentOutlet, CommonModule } from '@angular/common';
import { Event, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { DxDrawerModule, DxTreeViewComponent, DxTreeViewModule } from 'devextreme-angular';
import { HeaderComponent } from './header/header.component';
import { DxoScrollingModule } from 'devextreme-angular/ui/nested';
import { ConfigService } from './services/config.service';
import { AnalyticsService } from './services/analytics.service';
import { CataloguePlanningService } from './services/catalogue-planning.service';
import { firstValueFrom } from 'rxjs';
import { CataloguePlanningMethod } from './models/catalogue-planner.model';
import { NavigationItem } from './models/navigation-item.model';

/**
 * Menu items catalogue tenants don't use (GATE U2). Hidden by id, never by position:
 * Slotting, Core Range, Warehouse Pricing, Effectiveness, Slotting Types, Media Types,
 * Store Positions. Hiding is navigation only; the slotting routes have their own guard.
 */
export const CATALOGUE_HIDDEN_NAV_IDS: ReadonlySet<number> = new Set([11, 13, 14, 22, 41, 44, 45]);

/** Removes the given ids at any depth, keeping the rest of the menu structure intact. */
export function withoutNavItems(items: NavigationItem[], hidden: ReadonlySet<number>): NavigationItem[] {
  return items
    .filter(item => !hidden.has(item.id))
    .map(item => item.items ? { ...item, items: withoutNavItems(item.items, hidden) } : item);
}

// Declare global window interface for ZohoSalesIQ
declare global {
  interface Window {
    $zoho: any;
  }
}

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.css'],
    providers: [ RouterOutlet ],
    imports: [ RouterOutlet, CommonModule, DxDrawerModule, DxTreeViewModule, DxoScrollingModule, HeaderComponent ],
    standalone: true
})

export class AppComponent implements OnInit {
  @ViewChild(DxTreeViewComponent) treeView: DxTreeViewComponent;
  title = 'promoFlo';
  selectedItem: any;
  isDrawerOpen: boolean = false;
  isMaintenanceRoute = false;
  userDetails: any;
  navigation: NavigationItem[] = [];

  constructor(private router: Router,
    private cdr: ChangeDetectorRef,
    private configService: ConfigService,
    private analytics: AnalyticsService,
    private cataloguePlanning: CataloguePlanningService) {
    this.router.events.subscribe((event: Event) => {
      if (event instanceof NavigationEnd) {
        this.isMaintenanceRoute = event.urlAfterRedirects === '/maintenance';
        let route = this.router.routerState.snapshot.root;
        while (route.firstChild) route = route.firstChild;
        const path = route.routeConfig?.path ?? '';
        const template = path ? '/' + path : '/';
        this.analytics.trackPageView(template);
      }
    });

    // Get user details from localStorage or sessionStorage
    const userDetailsData = sessionStorage.getItem("user");
    if (userDetailsData) {
      this.userDetails = JSON.parse(userDetailsData);
    } else {
      this.userDetails = {};
    }
    this.cataloguePlanning.methodChanged$.subscribe(() => void this.initializeNavigation());
  }
  ngOnInit(): void {
    this.analytics.track('app:loaded');

    if (isDevMode()) {
      console.log('Development Mode');
    } else {
      console.log('Production Mode');
    }

    // Initialize navigation here (async — resolves the supplierPortal flag before adding its nav link)
    void this.initializeNavigation();
}

  
  private async initializeNavigation(): Promise<void> {
    let baseNavigation: NavigationItem[] = [
      { id: 0, text: "Home", icon: "home", path: '/' },
      { id: 1, text: "Planning", icon: "event", items: [
        {
          id: 11,
          text: 'Slotting',
          path: 'slotting'
        },
        {
          title: "Promotions",
          id: 12,
          text: 'Promotions',
          path: 'promotions'
        },
        {
          title: "Product Core Range",
          id: 13,
          text: 'Core Range',
          path: 'core-range'
        },
        {
          title: "Warehouse Pricing",
          id: 14,
          text: 'Warehouse Pricing',
          path: 'warehouse-pricing'
        }
        // Customer Pricing (id 15) is added below, gated behind the `supplierPortal` config flag.
      ]},
      { id: 2, text: "Analyse", icon: "textdocument", items: [
        {
          id: 21,
          text: 'Reports',
          path: 'reports'
        },
        {
          id: 22,
          text: 'Effectiveness',
          path: 'effectiveness'
        }
      ]},
      { id: 3, text: "Configuration", icon: "preferences", items: [
        {
          id: 31,
          text: 'Products',
          path: 'products'
        },
        {
          id: 32,
          text: 'Promotions',
          items: [
            {
              title: "Names",
              id: 321,
              text: 'Names',
              path: 'promotion-names'
            },
            {
              id: 322,
              text: 'Groups',
              path: 'promotion-groups'
            },
            {
              title: "Types",
              id: 323,
              text: 'Types',
              path: 'promotion-types'
            },
            {
              title: "Periods",
              id: 324,
              text: 'Periods',
              path: 'promotion-periods'
            },
            {
              title: "Status",
              id: 325,
              text: 'Status',
              path: 'promotion-status'
            },
            {
              title: "Allowances",
              id: 326,
              text: 'Allowances',
              path: 'promotion-allowances'
            },
            {
              title: "Categories",
              id: 327,
              text: 'Categories',
              path: 'promotion-categories'
            },
            {
              title: "Buying Dates",
              id: 328,
              text: 'Buying Dates',
              path: 'promotion-buying-dates'
            }
        ]},
        {
          id: 33,
          text: 'Other',
          items: [
            {
              title: "Slotting Types",
              id: 41,
              text: 'Slotting Types',
              path: 'slotting-types'
            },
            {
              title: "Buying Groups",
              id: 42,
              text: 'Buying Groups',
              path: 'buying-groups'
            },
            {
              title: "Events",
              id: 43,
              text: 'Events',
              path: 'events'
            },
            {
              title: "Media Types",
              id: 44,
              text: 'Media Types',
              path: 'media-types'
            },
            {
              title: "Store Positions",
              id: 45,
              text: 'Store Positions',
              path: 'store-positons'
            },
            {
              title: "Warehouses",
              id: 46,
              text: 'Warehouses',
              path: 'warehouses'
            },
            {
              title: "Host Data",
              id: 47,
              text: 'Host Data',
              path: 'raw-host-data'
            },
            {
              id: 34,
              text: 'Email Template',
              path: 'email-template'
            }
          ]
        }
      ]},
      { id: 4, text: "Support", icon: "help", path: "support" }
    ];

    const findItem = (id: number): NavigationItem | undefined => {
      const search = (items: NavigationItem[]): NavigationItem | undefined => {
        for (const item of items) {
          if (item.id === id) return item;
          const found = item.items ? search(item.items) : undefined;
          if (found) return found;
        }
        return undefined;
      };
      return search(baseNavigation);
    };

    // Add Promotion Export only if user is super user
    if (this.userDetails?.is_super_user === 1) {
      findItem(33)?.items?.push({ title: "Promotion Export", id: 48, text: 'Promotion Export', path: 'promotion-export' });
    }

    // Supplier Portal feature flag (config key `supplierPortal`) — when enabled, surface the
    // Customer Pricing link under Planning. The link stays hidden when the config is missing or
    // falsy ('', '0', 'false'); any other value ('1', 'true', …) counts as enabled.
    try {
      const supplierPortalConfig = await this.configService.GetConfigByKey('supplierPortal');
      const value = (supplierPortalConfig?.[0]?.config_string ?? '').toString().trim().toLowerCase();
      const supplierPortalEnabled = value !== '' && value !== '0' && value !== 'false';
      if (supplierPortalEnabled) {
        findItem(1)?.items?.push({ title: "Customer Pricing", id: 15, text: 'Customer Pricing', path: 'customer-pricing' });
      }
    } catch (error) {
      console.error('Error fetching supplierPortal config:', error);
      // Leave the Customer Pricing link hidden on error.
    }

    // Planning method is tenant-scoped and cached by the service. An unavailable method is
    // treated as slotting (the legacy menu); 401/403 also leave the legacy menu in place and
    // the auth guard handles the session.
    let planningMethod: CataloguePlanningMethod | null = null;
    try {
      planningMethod = await firstValueFrom(this.cataloguePlanning.getEffectiveMethod());
    } catch (error) {
      console.error('Error fetching promotion planning method:', error);
    }

    // The server-provided authorization decision is the sole gate for this tenant setting.
    if (planningMethod?.canManage) {
      findItem(33)?.items?.push({ title: 'Promotion Planning Method', id: 49, text: 'Planning Method', path: 'planning-method' });
    }

    if (planningMethod?.method === 'catalogue') {
      baseNavigation = withoutNavItems(baseNavigation, CATALOGUE_HIDDEN_NAV_IDS);
    }

    this.navigation = baseNavigation;
    console.log('Navigation initialized:', this.navigation);
  }

  toggleDrawer() {
    this.isDrawerOpen = !this.isDrawerOpen;
    console.log(this.isDrawerOpen);
    this.treeView.instance.collapseAll();
    this.cdr.detectChanges();
  }
  onOutsideClick = (e:any) => {
    // Check if the drawer is open
    if (this.isDrawerOpen) {
      // Close the drawer
      this.isDrawerOpen = false;
      // Collapse the tree view nodes
      this.collapseTreeView();
      this.cdr.detectChanges();
    }
    // Return true to proceed with the default outside click behavior
    return true;
  };
  collapseTreeView() {
    console.log(this.treeView)
    if (this.treeView && this.treeView.instance) {
      this.treeView.instance.collapseAll();
    }
  }
   async selectItem(item: any) {
    if (item.itemData?.disabled) return;
    if(!Object.keys(item.itemData).includes('items')){
      this.isDrawerOpen = !this.isDrawerOpen;
    }

    if('path' in item.itemData){
      let navigationPath = item.itemData.path;

      // Check if this is the Slotting menu item and conditionally route to V2
      if (item.itemData.id === 11 && navigationPath === 'slotting') {
        try {
          const configData = await this.configService.GetConfigByKey('slottingType');
          if (configData && configData.length > 0) {
            const configValue = configData[0].config_string;
            // slottingType: '1' = V1, '2' = V2 Weekly, '3' = V2 Monthly
            if (configValue === '2' || configValue === '3') {
              navigationPath = 'slotting-board-v2';
            }
          }
        } catch (error) {
          console.error('Error fetching slottingType config:', error);
          // Default to V1 if there's an error
        }
      }

      this.router.navigate([navigationPath]);
      this.isDrawerOpen = !this.isDrawerOpen;
    } else {
      this.selectedItem = item.itemData.component;
      if(!this.isDrawerOpen){
        this.isDrawerOpen = !this.isDrawerOpen;
      }
    }
    // Always close the drawer after selecting any item (except parent items with children)
    if(!Object.keys(item.itemData).includes('items')){
      this.closeDrawer();
    }
    this.cdr.detectChanges();
  }

  // Add this new method for closing the drawer
  closeDrawer() {
    this.isDrawerOpen = false;
    setTimeout(() => {
      this.collapseTreeView();
    }, 100);
    this.cdr.detectChanges();
  }
}

export class Tab {
  id: number;
  text: string;
  path: string;
}

